/**
 * Search and route planning UI for the starmap.
 *
 * The target survives reloads. Half the point of a destination is being able to
 * fly a leg, close the map, and come back to it still there — a target that
 * evaporated on refresh would have to be re-entered constantly, which is the
 * problem this is meant to solve.
 */

import {
  CLUSTER_JUMP_BASE,
  JUMP_RANGES,
  searchBodies,
  searchNames,
  type ClusterId,
  type NavTarget,
  Navigator,
  PLANET_TYPES,
  SIZE_CLASSES,
  PAD_LABELS,
  type PlanetType,
  type RoutePlan,
  type SearchQuery,
  type SearchResult,
  type SizeClass,
  type SystemId,
} from '@interspacial/shared';
import { useEffect, useMemo, useState } from 'react';

import { publishRoute } from './routeChannel';

const STORAGE_KEY = 'interspacial:navigation';

/**
 * Somewhere the player has decided to go.
 *
 * A `NavTarget` and nothing more: it was a system with a note attached, which
 * could not express "the moon Verath" or "the Ashfall cluster" — and those are
 * what a mission names and what a friend says. `NavTarget` already carries the
 * system every kind resolves to, so routing is unchanged; what it adds is the
 * ability to say *which part* of it was meant, which is the half the cockpit
 * needs to mark the right bracket.
 */
export type StoredTarget = NavTarget;

interface Persisted {
  /** Where the trip ends. */
  target: StoredTarget | null;
  /* `engine` was here. It is the hull's now — see `jumpRange`. */
  /**
   * Where it starts. **Read only to be discarded** — see `advanceStart`.
   *
   * Was a bare system id, because a start could only ever be a system. It is a
   * full target now so both ends of a journey are the same kind of thing and
   * the panel can offer one pair of buttons rather than two different ones.
   */
  start: StoredTarget | null;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { target: null, start: null };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return { target: parsed.target ?? null, start: parsed.start ?? null };
  } catch {
    // A corrupt entry should not brick the map.
    return { target: null, start: null };
  }
}

function save(state: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing and full quotas both throw. Losing the target is a
    // nuisance, not a failure worth interrupting anyone over.
  }
}

/**
 * The pinned body, if the destination is one.
 *
 * Read straight from storage rather than threaded through props, because the
 * only other reader is the cockpit — which is not mounted at the same time as
 * this panel, so there is nothing to keep in sync. The route channel carries
 * the *systems*; a route has no idea which moon within one you meant.
 */
type PinListener = (target: NavTarget | null) => void;
const pinListeners = new Set<PinListener>();

/**
 * Pin a destination from outside this panel.
 *
 * The chart can point at things this panel cannot search for — you steer to a
 * body and it is simply *there*, with no name typed — so the pinning has to be
 * available from the map itself. Written straight to storage and announced, so
 * the panel picks it up whether or not it happens to be open.
 */
export function pinTarget(target: NavTarget | null): void {
  const state = load();

  /*
   * A destination, and nothing else.
   *
   * A journey used to have two ends and the near one was a permanent source of
   * wrong answers: it defaulted to wherever the *camera* was, it went stale the
   * moment the ship jumped, and every caller that pinned a destination had to
   * remember to fill it in. There is no reason to plan a route from anywhere
   * other than where the ship is, so the origin is not stored at all now — it is
   * read from the ship at the moment a plan is made.
   */
  save({ ...state, target, start: null });
  for (const listener of pinListeners) listener(target);
}

/**
 * Move the journey's origin to where the ship actually is.
 *
 * A route is stored as two ends, and only the destination was ever chosen -- so
 * the origin stayed wherever it was pinned. Jump once and the chart went on
 * drawing the leg you had already flown, from a system you had left, while the
 * one you were in was not on the line at all.
 *
 * Called on arrival rather than on departure, so the origin is a fact about the
 * ship rather than a guess about the crossing. Does nothing when there is no
 * journey to re-anchor, and nothing when the origin already agrees -- both would
 * only write the same value back and wake every listener to say so.
 */
/**
 * Nothing to advance any more.
 *
 * The origin used to be stored and had to be dragged along behind the ship, so
 * arriving somewhere new republished the journey from the new system. Routes are
 * planned from where the ship *is* now, so there is nothing to keep in step —
 * kept as a no-op only so a caller that has not been updated does no harm.
 *
 * @deprecated The origin is not stored. Remove the call rather than this.
 */
export function advanceStart(_here: NavTarget | null): void {
  // Deliberately empty.
}

export function subscribePin(listener: PinListener): () => void {
  pinListeners.add(listener);
  return () => pinListeners.delete(listener);
}

/** The pinned destination, whatever kind it is. */
export function readTarget(): NavTarget | null {
  return load().target;
}

/** What the pinned destination is called, whatever kind it is. */
export function readTargetLabel(): string | null {
  return load().target?.label ?? null;
}

export function readTargetBody(): string | null {
  const target = load().target;
  return target?.kind === 'body' ? (target.bodyName ?? null) : null;
}

/**
 * One end of a journey: what is pinned there, and the one button that changes
 * it.
 *
 * Pin and unpin are the same control rather than two, because they are never
 * both meaningful — there is either something pinned or there is not, and a
 * greyed-out second button would be a permanent reminder of that.
 */
function JourneyEnd({
  label,
  value,
  candidate,
  onPin,
  onUnpin,
}: {
  label: string;
  /**
   * A loose shape, because one end is a real pinned target and the other is a
   * name for where the ship is. Only the two fields drawn below are read.
   */
  value: { label: string; detail?: string } | null;
  candidate: NavTarget | null;
  /** Absent on an end that cannot be chosen, which is where the ship is. */
  onPin?: () => void;
  onUnpin?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
      <span style={{ opacity: 0.5, fontSize: 10, width: 30, flexShrink: 0 }}>{label}</span>

      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          opacity: value ? 1 : 0.4,
        }}
        title={value ? `${value.label} — ${value.detail}` : undefined}
      >
        {value ? value.label : 'not set'}
      </span>

      {/* No buttons on an end nobody chooses. Where the ship is is a fact, and
          a `pin`/`unpin` beside a fact invites a question that has no answer. */}
      {!onPin && !onUnpin ? null : value ? (
        <button type="button" style={BUTTON} onClick={onUnpin}>
          unpin
        </button>
      ) : (
        <button
          type="button"
          style={{ ...BUTTON, opacity: candidate ? 1 : 0.35 }}
          disabled={!candidate}
          onClick={onPin}
          title={candidate ? `Pin ${candidate.label}` : 'Point the chart at something first'}
        >
          pin
        </button>
      )}
    </div>
  );
}

/** Where the ship is, as a journey's starting point. */
export function whereWeAre(navigator: Navigator, systemId: SystemId | null): NavTarget | null {
  const here = systemId ? navigator.get(systemId) : null;
  if (!here) return null;

  return {
    kind: 'system',
    label: here.name,
    detail: 'where you are',
    systemId: here.id,
    clusterId: here.clusterId as ClusterId,
  };
}

const PANEL: React.CSSProperties = {
  position: 'absolute',
  background: 'rgba(8, 14, 24, 0.88)',
  border: '1px solid rgba(127, 212, 255, 0.22)',
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.5,
};

const FIELD: React.CSSProperties = {
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgba(127,212,255,0.25)',
  color: '#e2e2e2',
  font: 'inherit',
  fontSize: 12,
  padding: '3px 5px',
  width: '100%',
};

const BUTTON: React.CSSProperties = {
  background: 'rgba(127,212,255,0.12)',
  border: '1px solid rgba(127,212,255,0.3)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 11,
  padding: '3px 8px',
  cursor: 'pointer',
};

type Category = 'station' | 'planet' | 'asteroid';

export interface SearchPanelProps {
  navigator: Navigator;
  /** Where the camera currently is, used as the default origin. */
  focusedSystemId: SystemId | null;
  onGoTo: (systemId: SystemId) => void;
  /**
   * Whatever the chart is pointing at, ready to be pinned to either end.
   *
   * Supplied rather than worked out here because only the map knows what the
   * pivot has caught — a moon it snapped to has no name in this panel until it
   * is handed one.
   */
  underCursor?: NavTarget | null;
  /** Where the ship actually is, for auto-filling the start of a journey. */
  hereSystemId?: SystemId | null;
  /**
   * What the ship can cross in one hop.
   *
   * Read off the hull rather than chosen here. A tier select made the route a
   * question about a ship you might own instead of a plan for the one you are
   * flying — and it could be set to something the drive cannot do, which is a
   * plan that lies.
   */
  jumpRange?: number;
  /** And what it can cross between clusters, which is a separate reach. */
  clusterRange?: number;
}

export function SearchPanel({
  navigator,
  focusedSystemId,
  onGoTo,
  underCursor = null,
  hereSystemId = null,
  jumpRange = JUMP_RANGES.basic,
  clusterRange = CLUSTER_JUMP_BASE,
}: SearchPanelProps) {
  const [stored, setStored] = useState<Persisted>(() => load());
  const [category, setCategory] = useState<Category>('station');
  const [minPad, setMinPad] = useState<SizeClass | ''>('');
  const [needsFabricator, setNeedsFabricator] = useState(false);
  const [planetType, setPlanetType] = useState<PlanetType | ''>('');
  const [fieldKind, setFieldKind] = useState<'' | 'cluster' | 'belt'>('');
  const [results, setResults] = useState<SearchResult[] | null>(null);

  /**
   * Looking for a *named* place, rather than for a kind of place.
   *
   * Kept beside the criteria search rather than replacing it, because they are
   * two different questions. "Where is the nearest large pad" is what you ask
   * when you are looking for work; "take me to Verath" is what you ask when a
   * mission or a friend has already decided for you, and until now the map
   * could only answer the first.
   */
  const [name, setName] = useState('');
  const [named, setNamed] = useState<NavTarget[] | null>(null);

  /**
   * Which question is being asked.
   *
   * They are genuinely different jobs and were sharing one column: "take me to
   * Verath" needs a text field and nothing else, while "find me a large pad"
   * needs four selects. Stacked, each was scrolling the other off the panel.
   */
  const [tab, setTab] = useState<'search' | 'near'>('search');

  // Something was pinned from the chart rather than from in here. Re-read
  // rather than patched, because `pinTarget` may have filled the start in too.
  useEffect(() => subscribePin(() => setStored(load())), []);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => save(stored), [stored]);

  /*
   * Always the ship.
   *
   * This was "whichever end has been pinned, falling back to what the camera is
   * looking at", which produced routes between two places the ship was not —
   * most visibly when a mission board pinned a destination and the chart planned
   * it from whatever system the map happened to be centred on.
   *
   * There is no reason to plan from anywhere else. The fallback to the focused
   * system remains only for the case where the ship's position is genuinely
   * unknown to this panel, which is the menu.
   */
  const origin = hereSystemId ?? focusedSystemId;
  const originName = origin ? navigator.get(origin)?.name : null;

  /**
   * Pin whatever the chart is on to one end of the journey.
   *
   * Pinning a destination with no start fills the start in from where the ship
   * actually is, because that is what was meant every time: a journey has two
   * ends and nobody plots one from somewhere they are not. Doing it here rather
   * than making the player click twice is the difference between a plan and a
   * form.
   */
  const pin = (end: 'start' | 'target') => {
    if (!underCursor) return;

    if (end === 'start') {
      // Nothing pins a start any more. Left as an early return rather than
      // removed from the type, so a stray caller is a no-op and not a crash.
      return;
    }

    // Through the shared path, so pinning from here and pinning from the chart
    // cannot drift apart. Only a destination goes in — the route is planned from
    // wherever the ship is when it is planned.
    pinTarget(underCursor);
    setStored(load());
  };

  /**
   * Where the journey actually has to reach.
   *
   * A cluster is satisfied by *any* system inside it, so it resolves to
   * whichever is nearest the start rather than to a fixed one. Picking the
   * cluster's centre — which is what the target itself carries — would route a
   * player past the edge of the cluster they were already at and on into the
   * middle of it for no reason.
   */
  const destination = useMemo(() => {
    const target = stored.target;
    if (!target) return null;
    if (target.kind !== 'cluster' || !origin) return target.systemId;

    const from = navigator.get(origin);
    if (!from) return target.systemId;

    let best = target.systemId;
    let bestGap = Infinity;

    for (const system of navigator.systemsIn(target.clusterId)) {
      const gap = Math.hypot(
        system.galacticPosition.x - from.galacticPosition.x,
        system.galacticPosition.y - from.galacticPosition.y,
        system.galacticPosition.z - from.galacticPosition.z,
      );
      if (gap < bestGap) {
        bestGap = gap;
        best = system.id;
      }
    }
    return best;
  }, [navigator, origin, stored.target]);

  const plan: RoutePlan | null = useMemo(() => {
    if (!origin || !destination) return null;
    return navigator.planRoute(origin, destination, jumpRange, clusterRange);
  }, [navigator, origin, destination, jumpRange, clusterRange]);

  // Hand the route to the scene. Published through a channel rather than a
  // prop because the scene lives inside an effect that must not re-run.
  useEffect(() => publishRoute(plan), [plan]);

  const runSearch = () => {
    if (!origin) return;
    setSearching(true);

    const query: SearchQuery =
      category === 'station'
        ? { kind: 'station', ...(minPad ? { minPad } : {}), ...(needsFabricator ? { needsAdvancedFabricator: true } : {}) }
        : category === 'planet'
          ? { kind: 'planet', ...(planetType ? { planetType } : {}) }
          : { kind: 'asteroid', ...(fieldKind ? { field: fieldKind } : {}) };

    // Deferred a frame so the button visibly changes to "searching" before the
    // main thread is taken over by generation.
    setTimeout(() => {
      setResults(navigator.search(origin, query, { limit: 12 }));
      setSearching(false);
    }, 0);
  };

  /**
   * Run the name search.
   *
   * Cluster and system names come off the galaxy map and are effectively free.
   * Bodies are not — a name does not exist until its system is generated — so
   * they are searched only within the cluster the camera is in, which is both
   * bounded and the place a player naming a body almost certainly means.
   */
  const runNameSearch = () => {
    const query = name;

    if (query.trim().length < 2) {
      setNamed(null);
      return;
    }

    const places = searchNames(navigator.seed, query);

    const cluster = origin ? navigator.get(origin)?.clusterId : null;
    const bodies = cluster
      ? searchBodies(
          navigator.seed,
          query,
          navigator.systemsIn(cluster).map((system) => system.id),
        )
      : [];

    // Named places first: a system called Verath is a likelier answer to
    // "Verath" than a moon in the cluster you happen to be looking at.
    setNamed([...places, ...bodies].slice(0, 12));
  };

  const setTarget = (result: SearchResult) => {
    setStored((s) => ({
      ...s,
      target: {
        kind: 'system',
        label: result.system.name,
        detail: result.label,
        systemId: result.system.id,
        clusterId: result.system.clusterId as ClusterId,
      },
    }));
  };

  if (!open) {
    return (
      <div style={{ ...PANEL, bottom: 16, left: 16, padding: '5px 10px' }}>
        <button type="button" style={BUTTON} onClick={() => setOpen(true)}>
          navigation
        </button>
      </div>
    );
  }

  return (
    /* Bottom left, opposite the exits.
       It used to sit directly above "back to the ship", so opening it covered
       the control you would use next — and the two things on that side of the
       screen did entirely different jobs. Navigation is where you *plan*;
       leaving is on the other corner. */
    <div style={{ ...PANEL, bottom: 16, left: 16, width: 300, maxHeight: '78vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Navigation</strong>
        <button type="button" style={BUTTON} onClick={() => setOpen(false)}>
          hide
        </button>
      </div>

      {/* ── Which question ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {(['search', 'near'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setTab(option)}
            style={{
              ...BUTTON,
              flex: 1,
              background: tab === option ? 'rgba(127,212,255,0.2)' : 'transparent',
              opacity: tab === option ? 1 : 0.6,
            }}
          >
            {option === 'search' ? 'search' : 'find near me'}
          </button>
        ))}
      </div>

      {/* ── By name ────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 10, display: tab === 'search' ? 'block' : 'none' }}>
        {/* Run on demand rather than per keystroke. Body search generates every
            system in the cluster, so typing a six-letter name was doing that
            six times — which is the lag, and it is entirely avoidable by
            waiting to be asked. */}
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && runNameSearch()}
            placeholder="cluster, system or body…"
            style={{ ...FIELD, flex: 1, minWidth: 0 }}
          />
          <button type="button" style={BUTTON} onClick={runNameSearch}>
            find
          </button>
        </div>

        {named && named.length === 0 && (
          <div style={{ opacity: 0.5, marginTop: 6 }}>Nothing by that name near here.</div>
        )}

        {named?.map((target) => (
          <button
            key={`${target.kind}-${target.systemId}-${target.bodyName ?? ''}`}
            type="button"
            onClick={() => {
              setStored((current) => ({ ...current, target }));
              setNamed(null);
              setName('');
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginTop: 4,
              padding: '4px 6px',
              background: 'transparent',
              border: '1px solid rgba(127,212,255,0.16)',
              color: '#dbeaff',
              font: 'inherit',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <span style={{ opacity: 0.45, fontSize: 9.5, letterSpacing: 1 }}>
              {target.kind.toUpperCase()}
            </span>{' '}
            {target.label}
            <div style={{ opacity: 0.55, fontSize: 11 }}>{target.detail}</div>
          </button>
        ))}
      </div>

      {/* ── Find near me ───────────────────────────────────────────────── */}
      <div style={{ display: tab === 'near' ? 'block' : 'none' }}>
      {/* The engine tier used to be chosen here. It comes off the hull now
          — see `jumpRange` — because a route planned for a drive you do not
          have is a plan that lies. */}

      {/* ── Query ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {(['station', 'planet', 'asteroid'] as Category[]).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              setCategory(c);
              setResults(null);
            }}
            style={{
              ...BUTTON,
              flex: 1,
              background: category === c ? 'rgba(127,212,255,0.28)' : 'transparent',
            }}
          >
            {c}
          </button>
        ))}
      </div>



      {category === 'station' && (
        <>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <span style={{ opacity: 0.6 }}>minimum pad</span>
            <select style={FIELD} value={minPad} onChange={(e) => setMinPad(e.target.value as SizeClass | '')}>
              <option value="">any</option>
              {SIZE_CLASSES.map((size) => (
                <option key={size} value={size}>
                  {PAD_LABELS[size]} — {size}
                </option>
              ))}
            </select>
          </label>
          {/* Fabricators are the scarce capability worth searching for: they
              are the only place a line's length can be changed. */}
          <label style={{ display: 'block', marginBottom: 8, opacity: 0.8 }}>
            <input
              type="checkbox"
              checked={needsFabricator}
              onChange={(e) => setNeedsFabricator(e.target.checked)}
            />{' '}
            has an advanced fabricator
          </label>
        </>
      )}

      {category === 'planet' && (
        <label style={{ display: 'block', marginBottom: 8 }}>
          <span style={{ opacity: 0.6 }}>type</span>
          <select
            style={FIELD}
            value={planetType}
            onChange={(e) => setPlanetType(e.target.value as PlanetType | '')}
          >
            <option value="">any</option>
            {PLANET_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      )}

      {category === 'asteroid' && (
        <label style={{ display: 'block', marginBottom: 8 }}>
          <span style={{ opacity: 0.6 }}>kind</span>
          <select
            style={FIELD}
            value={fieldKind}
            onChange={(e) => setFieldKind(e.target.value as '' | 'cluster' | 'belt')}
          >
            <option value="">any</option>
            <option value="cluster">ring around a planet</option>
            <option value="belt">belt around the star</option>
          </select>
        </label>
      )}

      <button
        type="button"
        style={{ ...BUTTON, width: '100%', padding: '5px 8px' }}
        disabled={!origin || searching}
        onClick={runSearch}
      >
        {searching ? 'searching…' : origin ? 'search' : 'focus a system first'}
      </button>

      {/* ── Results ────────────────────────────────────────────────────── */}
      {results && (
        <div style={{ marginTop: 10 }}>
          {results.length === 0 && <div style={{ opacity: 0.6 }}>nothing found nearby</div>}

          {results.map((result) => {
            const sameCluster = navigator.get(origin!)?.clusterId === result.system.clusterId;
            return (
              <div
                key={result.system.id}
                style={{
                  borderTop: '1px solid rgba(127,212,255,0.14)',
                  padding: '5px 0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div>{result.system.name}</div>
                  <div style={{ opacity: 0.6, fontSize: 11 }}>{result.label}</div>
                  <div style={{ opacity: 0.45, fontSize: 10 }}>
                    {Math.round(result.distance).toLocaleString()} away
                    {!sameCluster && ' · needs a warp'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <button type="button" style={BUTTON} onClick={() => setTarget(result)}>
                    target
                  </button>
                  <button type="button" style={BUTTON} onClick={() => onGoTo(result.system.id)}>
                    show
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
      {/* ── Current journey ────────────────────────────────────────────── */}
      <div
        style={{
          border: '1px solid rgba(190,120,255,0.28)',
          background: 'rgba(190,120,255,0.06)',
          padding: '7px 8px',
          marginBottom: 10,
        }}
      >
        <div style={{ opacity: 0.6, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
          current journey
        </div>

        {/* One end, because a journey only ever has one worth choosing.
            The other is wherever the ship is, read at the moment the route is
            planned rather than pinned and dragged along behind it. */}
        <JourneyEnd
          label="from"
          value={originName ? { label: originName, detail: 'where you are' } : null}
          candidate={null}
        />
        <JourneyEnd
          label="to"
          value={stored.target}
          candidate={underCursor}
          onPin={() => pin('target')}
          onUnpin={() => setStored((current) => ({ ...current, target: null }))}
        />

        {/* What the trip costs, in the same panel as the ends of it.
            These were two boxes about one thing — you pinned the ends in the
            first and read what it would take in the second, a hand's width
            apart with the destination named on both. */}
        {stored.target && (
          <div style={{ marginTop: 8, borderTop: '1px solid rgba(190,120,255,0.2)', paddingTop: 7 }}>
            <div style={{ opacity: 0.7, fontSize: 11 }}>{stored.target.detail}</div>

            {plan ? (
              <div style={{ marginTop: 5, opacity: 0.85 }}>
                <div>
                  {plan.jumps} jump{plan.jumps === 1 ? '' : 's'}
                  {plan.warps > 0 && ` · ${plan.warps} core${plan.warps === 1 ? '' : 's'}`}
                </div>

                {/* The next hop is the only part that matters right now. */}
                {plan.path[1] && (
                  <div style={{ marginTop: 4 }}>
                    next: <strong>{navigator.get(plan.path[1])?.name}</strong>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 5, opacity: 0.6 }}>
                {origin ? 'no route from here' : 'nowhere to plan from'}
              </div>
            )}

            {/* Either end, reachable at any point along the trip — checking
                what you are heading for and where you set off from are both
                things you want mid-route. */}
            <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
              <button
                type="button"
                style={BUTTON}
                disabled={!origin}
                onClick={() => origin && onGoTo(origin)}
              >
                here
              </button>
              <button
                type="button"
                style={BUTTON}
                onClick={() => destination && onGoTo(destination)}
              >
                target
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
